import React, { useState } from 'react';
import { Eye, Pencil, Trash2 } from 'lucide-react';
import { useKeyboardNavigation } from '../../../hooks/useAccessibility';

interface TemplateCardProps {
  template: any;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onPreview: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  isDeletable?: boolean;
}

const NewTemplateCard: React.FC<TemplateCardProps> = ({ template, onPreview, onEdit, onDelete, isDeletable = true, }) => {

  const [imageError, setImageError] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const name: string = template?.name || 'Untitled Template';
  const category: string = template?.category || template?.tag || '';
  const thumbnailSrc: string =
    (template?.thumbnail && String(template.thumbnail).trim()) ||
    (template?.thumbnail_url && String(template.thumbnail_url).trim()) || '';

  useKeyboardNavigation(() => onEdit(), () => { });

  return (
    <article
      onClick={onEdit}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(); } }}
      tabIndex={0}
      role="button"
      aria-label={`Template: ${name}`}
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        overflow: 'hidden',
        cursor: 'pointer',
        background: '#fff',
        transition: 'box-shadow 0.15s ease, transform 0.15s ease',
      }}
      onMouseEnter={(e) => { setShowActions(true); (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 16px rgba(0,0,0,0.10)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
      onMouseLeave={(e) => { setShowActions(false); (e.currentTarget as HTMLElement).style.boxShadow = ''; (e.currentTarget as HTMLElement).style.transform = ''; }}
    >
      {/* Thumbnail */}
      <div style={{ position: 'relative', background: '#f3f4f6', height: 220, overflow: 'hidden' }}>
        {!imageError && thumbnailSrc ? (
          <img
            src={thumbnailSrc}
            alt={name}
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
              objectFit: 'cover',
              objectPosition: 'top',
            }}
            onError={() => setImageError(true)}
          />
        ) : (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 12 }}>
            No preview
          </div>
        )}

        {/* Hover actions */}
        {showActions && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            gap: 6, paddingBottom: 10,
          }}>
            <button
              onClick={(e) => { e.stopPropagation(); onPreview(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', cursor: 'pointer' }}
            >
              <Eye size={12} /> Preview
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#2563eb', border: 'none', color: '#fff', cursor: 'pointer' }}
            >
              <Pencil size={12} /> Edit
            </button>
          </div>
        )}
      </div>

      <div className="py-2.5 px-3 flex items-center justify-between gap-2"
        style={{
          background: '#ffffff',
          borderTop: '1px solid #e5e7eb',
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          {/* Template name — darker, bolder */}
          <div style={{
            fontSize: 12.5,
            fontWeight: 700,
            color: '#111827',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            letterSpacing: '-0.01em',
          }}>
            {name}
          </div>
          {/* Category badge */}
          {category && (
            <span style={{
              display: 'inline-block',
              marginTop: 4,
              fontSize: 10,
              fontWeight: 600,
              color: '#4f46e5',
              background: '#eef2ff',
              border: '1px solid #c7d2fe',
              borderRadius: 5,
              padding: '1px 6px',
              letterSpacing: '0.01em',
              textTransform: 'capitalize',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {category}
            </span>
          )}
        </div>

        {isDeletable && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            title="Delete"
            style={{ flexShrink: 0, padding: 5, borderRadius: 6, border: 'none', background: 'transparent', color: '#d1d5db', cursor: 'pointer', display: 'flex', transition: 'color 0.15s, background 0.15s' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#ef4444'; (e.currentTarget as HTMLButtonElement).style.background = '#fef2f2'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#d1d5db'; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>
    </article>
  );
};

export default NewTemplateCard;
